import React, { useCallback, useMemo, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileImport, faFolders, faMinus, IconName } from '@fortawesome/pro-regular-svg-icons';
import { OptionType, useModal } from '@europrocurement/flexy-components';
import {
    PROCESS_STATUS_FLAG_ONGOING,
    REFRESH_CURRENT_DOSSIER_CTX,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
    useDossierDocumentsCtx,
    useDossierProcesses,
    useDossierProcessesActionsAndRender,
    useDossierValidatorsCtx,
    VALIDATOR_DEMAT_FACILE,
    VALIDATOR_TYPE_GREFFE,
} from '@europrocurement/l2d-modules';
import { useSnackbar } from 'notistack';
import { DossierOpenSearch } from '@europrocurement/l2d-domain';
import DossierFormaliteProcessListRows from './DossierFormaliteProcessListRows';
import DossierFormaliteProcessListHeaderCells from './DossierFormaliteProcessListHeaderCells';
import DissociateFormalitiesForm from '../DissociateFormalitiesForm/DissociateFormalitiesForm';
import MoveFormalitiesForm from '../MoveFormalitiesToOtherDossierForm/MoveFormalitiesToOtherDossierForm';
import UpdateValidatorForm from '../UpdateValidatorForm/UpdateValidatorForm';
import NavigateToExternalUrlModal from '../NavigateToExternalUrlModal/NavigateToExternalUrlModal';

export type DossierFormaliteProcessProps = {
    libelle: string;
    documentsCount: number;
    complete: boolean;
    optionsCount: number;
    externalUrl?: string;
    onExternalUrlClick: (externalUrl: string) => void;
    id: number;
};

export type DossierFormaliteProcessesListProps = {
    formalities: DossierFormaliteProcessProps[];
    publishable: boolean;
};

export type SelectedFormality = {
    id: number;
};

const DossierFormaliteProcessesList: React.FunctionComponent<DossierFormaliteProcessesListProps> =
    function (props) {
        const { formalities, publishable } = props;
        const { palette } = useTheme();
        const { modalActions } = useModal();
        const { getDossierDocuments } = useDossierDocumentsCtx();
        const { dossier, siblings } = useCurrentDossierCtx();
        const dispatch = useCurrentDossierCtxDispatch();

        const refreshDossier = useCallback(() => {
            if (dispatch) {
                dispatch({ type: REFRESH_CURRENT_DOSSIER_CTX });
            }
        }, [dispatch]);
        const { enqueueSnackbar } = useSnackbar();
        const {
            hasOnGoingProcesses,
            getCurrentProcessOfType,
            canChangeProcessStatus,
            toggleGreffeValidator,
            canToggleGreffeValidator,
        } = useDossierProcesses();
        const { actionUpdateValidatorStatus } = useDossierProcessesActionsAndRender();

        const process = useMemo(
            () => (dossier ? getCurrentProcessOfType(dossier, VALIDATOR_TYPE_GREFFE) : undefined),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [dossier],
        );

        const { getDossierValidatorById } = useDossierValidatorsCtx();
        const statuses = process
            ? getDossierValidatorById(process.validatorId)?.statuses || []
            : [];
        const actions = [];

        const filteredSiblings =
            (siblings && dossier && siblings.filter((doss) => doss.id !== dossier.id)) || [];

        const [selectedFormalitiesIds, setSelectedFormalitiesIds] = useState<number[]>([]);

        const handleOnUpdateStatus = () => {
            if (!process || !canChangeProcessStatus(process)) {
                return;
            }

            modalActions.call(
                <UpdateValidatorForm
                    processing={false}
                    process={process}
                    onItemSelected={(newProcess) => {
                        refreshDossier();

                        if (newProcess && newProcess.externalUrl) {
                            modalActions.call(
                                <NavigateToExternalUrlModal
                                    externalUrl={newProcess.externalUrl as string}
                                />,
                            );
                        } else {
                            modalActions.close();
                        }
                    }}
                    statuses={statuses.filter(
                        (status) =>
                            status.statusFlag !== PROCESS_STATUS_FLAG_ONGOING || publishable,
                    )}
                />,
            );
        };

        const handleDissociate = () => {
            if (!dossier) {
                return;
            }
            // check if dossier has running validators
            if (hasOnGoingProcesses(dossier.validationProcesses)) {
                enqueueSnackbar(
                    'Des processus de validation sont en cours sur le dossier, dissociation impossible',
                    { variant: 'error' },
                );
                return;
            }

            modalActions.call(
                <DissociateFormalitiesForm
                    formalitiesIds={selectedFormalitiesIds}
                    dossier={dossier}
                    documents={getDossierDocuments()}
                    onSubmitDone={refreshDossier}
                />,
            );
        };

        const handleMove = () => {
            if (!dossier) {
                return;
            }
            // check if dossier has running validators
            if (hasOnGoingProcesses(dossier.validationProcesses)) {
                enqueueSnackbar(
                    'Des processus de validation sont en cours sur le dossier, déplacement impossible',
                    { variant: 'error' },
                );
                return;
            }
            const options = filteredSiblings.map((doss) => {
                const nbrFormalities = doss.formalites.length;

                return {
                    value: doss.id,
                    label: `${doss.numero} - ${doss.societeDenomination} - ${
                        nbrFormalities > 1
                            ? `${doss.formalites[0].libelle} + ${nbrFormalities - 1} `
                            : doss.formalites[0].libelle
                    }`,
                } as OptionType;
            });

            modalActions.call(
                <MoveFormalitiesForm
                    formalitiesIds={selectedFormalitiesIds}
                    dossier={dossier}
                    documents={getDossierDocuments()}
                    onSubmitDone={refreshDossier}
                    options={options}
                />,
            );
        };

        const handleToggleValidator = async () => {
            if (!process) {
                return;
            }

            try {
                await toggleGreffeValidator(dossier as DossierOpenSearch, process);
                refreshDossier();
            } catch (e) {
                enqueueSnackbar('Une erreur est survenue', { variant: 'error' });
                console.error(e);
            }
        };

        const allowChangeProcessStatus = process && canChangeProcessStatus(process);

        if (allowChangeProcessStatus) {
            actions.push(actionUpdateValidatorStatus(handleOnUpdateStatus));
        }

        if (
            process &&
            dossier &&
            process.validatorTypeId === VALIDATOR_TYPE_GREFFE &&
            canToggleGreffeValidator(dossier)
        ) {
            actions.push({
                label:
                    process.validatorId === VALIDATOR_DEMAT_FACILE
                        ? 'Passer en Guichet Unique'
                        : 'Passer en Demat Facile',
                icon: 'rotate-reverse' as IconName,
                onClick: handleToggleValidator,
            });
        }

        return (
            <Box
                sx={{
                    borderCollapse: 'separate',
                    borderSpacing: '0 4px',
                }}
            >
                <Box>
                    <DossierFormaliteProcessListHeaderCells
                        isMulipleFormalities={formalities.length > 1}
                        showStatus={process !== undefined}
                    />
                </Box>

                {/* If selected rows it display the action menu */}
                {selectedFormalitiesIds.length > 0 && formalities.length > 1 && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            gap: '8px',
                            paddingLeft: '19px',
                        }}
                    >
                        <Box
                            sx={{
                                border: `${palette.primary.main} 1px solid`,
                                width: '20px',
                                height: '20px',
                                borderRadius: '6px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                            onClick={() => setSelectedFormalitiesIds([])}
                        >
                            <FontAwesomeIcon icon={faMinus} />
                        </Box>
                        <Typography
                            variant="l2dcaption"
                            sx={{ display: 'block' }}
                        >
                            {selectedFormalitiesIds.length} sélection(s)
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                            }}
                            onClick={() => handleDissociate()}
                        >
                            <FontAwesomeIcon icon={faFolders} />
                            Dissocier
                        </Box>

                        {/* Move to an other dossier from the collection */}
                        {filteredSiblings.length > 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '8px',
                                    cursor: 'pointer',
                                }}
                                onClick={() => handleMove()}
                            >
                                <FontAwesomeIcon icon={faFileImport} />
                                Déplacer
                            </Box>
                        )}
                    </Box>
                )}

                <Box>
                    <DossierFormaliteProcessListRows
                        formalities={formalities}
                        isMulipleFormalities={formalities.length > 1}
                        selectedFormalitiesIds={selectedFormalitiesIds}
                        setSelectedFormalitiesIds={setSelectedFormalitiesIds}
                        actions={actions}
                        process={
                            process
                                ? {
                                      validatorId: process.validatorId as number,
                                      validatorLabel: process.validatorLabel as string,
                                      statusLabel: process.statusLabel,
                                      statusFlag: process.statusFlag,
                                      onStatusClick: allowChangeProcessStatus
                                          ? handleOnUpdateStatus
                                          : undefined,
                                  }
                                : undefined
                        }
                    />
                </Box>
            </Box>
        );
    };

export default DossierFormaliteProcessesList;
